import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { inr, fmtDate } from "../../lib/format";
import { StatusBadge } from "../../components/StatusBadge";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { downloadCSV } from "../../lib/csv";
import { Search, Download } from "lucide-react";

export default function AdminTransactions() {
  const [txns, setTxns] = useState([]);
  const [type, setType] = useState("all");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (type !== "all") p.append("type", type);
    if (status !== "all") p.append("status", status);
    if (search) p.append("search", search);
    if (from) p.append("date_from", from);
    if (to) p.append("date_to", to);
    api.get(`/admin/transactions?${p.toString()}`).then((r) => setTxns(r.data));
  }, [type, status, search, from, to]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const exportCsv = () => {
    downloadCSV(
      "swiftors-all-transactions.csv",
      [
        { label: "Date & Time", value: (r) => fmtDate(r.created_at) },
        { label: "User", value: (r) => r.user_name || r.user_email },
        { label: "Email", value: "user_email" },
        { label: "Type", value: "type" },
        { label: "Amount", value: "amount" },
        { label: "UTR", value: (r) => r.utr || "" },
        { label: "Status", value: "status" },
      ],
      txns
    );
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <p className="overline">Ledger</p>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight mt-1">All Transactions</h1>
        </div>
        <Button data-testid="admin-txn-export" variant="outline" onClick={exportCsv} className="rounded-sm h-10 gap-2"><Download size={15} /> Export CSV</Button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <div className="relative lg:col-span-1 sm:col-span-2">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="admin-txn-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="User / UTR / amount" className="pl-9 rounded-sm h-10" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="admin-txn-type" className="rounded-sm h-10"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="deposit">Deposit</SelectItem><SelectItem value="withdrawal">Withdrawal</SelectItem><SelectItem value="adjustment">Adjustment</SelectItem></SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="admin-txn-status" className="rounded-sm h-10"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="processing">Processing</SelectItem><SelectItem value="approved">Approved</SelectItem><SelectItem value="paid">Paid</SelectItem><SelectItem value="rejected">Rejected</SelectItem></SelectContent>
        </Select>
        <Input data-testid="admin-txn-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-sm h-10" />
        <Input data-testid="admin-txn-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-sm h-10" />
      </div>

      <div className="border border-border rounded-sm overflow-x-auto bg-card">
        <table className="w-full text-sm" data-testid="admin-transactions-table">
          <thead><tr className="text-left overline border-b border-border">
            <th className="px-5 py-3 font-semibold">User</th><th className="px-5 py-3 font-semibold">Type</th>
            <th className="px-5 py-3 font-semibold">Amount</th><th className="px-5 py-3 font-semibold">UTR</th>
            <th className="px-5 py-3 font-semibold hidden md:table-cell">Date &amp; Time</th><th className="px-5 py-3 font-semibold">Status</th>
          </tr></thead>
          <tbody>
            {txns.length === 0 ? (<tr><td colSpan="6" className="px-5 py-6 text-muted-foreground">No transactions found.</td></tr>) : txns.map((t) => (
              <tr key={t.id} className="border-b border-border/40 last:border-0 hover:bg-white/5">
                <td className="px-5 py-3">{t.user_name || t.user_email}</td>
                <td className="px-5 py-3 capitalize">{t.type}</td>
                <td className="px-5 py-3 font-mono-num font-medium">{inr(t.amount)}</td>
                <td className="px-5 py-3 font-mono-num text-xs">{t.utr || "—"}</td>
                <td className="px-5 py-3 text-xs text-muted-foreground hidden md:table-cell">{fmtDate(t.created_at)}</td>
                <td className="px-5 py-3"><StatusBadge status={t.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
